// @flow
import { useEffect, useState } from 'react';
import Debug from 'debug';
import Event from 'ol/events/Event';
import OlDraw from 'ol/interaction/Draw';
import type { GeometryType, DrawInteraction, DrawEvent, Source, Style, StyleFunction } from '../types';
import { useMap } from '../context';
import { useLayer } from '../layers/context';
import { useSource } from '../sources/context';
import useEvent from './useEvent';

const debug = Debug('amms:modules:openlayers:interactions:draw');

class CustomDraw extends OlDraw {
    addToDrawing_(event) {
        super.addToDrawing_(event);

        const drawEvent = new Event('draw');
        drawEvent.feature = this.sketchFeature_;
        this.dispatchEvent(drawEvent);
    }
}

type Props = $ReadOnly<{
    label?: string,
    type: GeometryType,
    source?: Source,
    geometryFunction?: (coordinates) => void,
    style: Style | StyleFunction,

    onChange?: (e: DrawEvent) => void,
    onDrawStart?: (e: DrawEvent) => void,
    onDraw?: (e: Event) => void,
    onDrawEnd?: (e: DrawEvent) => void,
    onPropertyChange?: (e: DrawEvent) => void
}>;

function Draw({ label = '', type, source: externalSource, geometryFunction, style, onChange, onDrawStart, onDraw, onDrawEnd, onPropertyChange }: Props) {
    const [draw, setDraw] = useState();
    const map = useMap();
    const layer = useLayer();
    const source = useSource();
    const _source = externalSource || source || (layer && layer.getSource());

    useEffect(() => {
        let _draw: DrawInteraction;
        if(map && _source) {
            if(layer) {
                debug(`Adding draw interaction ${label} to layer: `, layer);
            }
            else {
                debug(`Adding draw interaction ${label} to map`);
            }

            _draw = new CustomDraw({
                label,
                type,
                source: _source,
                geometryFunction,
                style
            });

            map.addInteraction(_draw);
            setDraw(_draw);
        }

        return () => {
            if(map && _draw) {
                if(layer) {
                    debug(`Removing draw interaction ${label} from layer: `, layer);
                }
                else {
                    debug(`Removing draw interaction ${label} from map`);
                }

                map.removeInteraction(_draw);
                setDraw(null);
            }
        };
    }, [label, map, layer, type, _source, geometryFunction, style]);


    useEvent(draw, 'change', onChange);
    useEvent(draw, 'drawstart', onDrawStart);
    useEvent(draw, 'draw', onDraw);
    useEvent(draw, 'drawend', onDrawEnd);
    useEvent(draw, 'propertychange', onPropertyChange);

    return null;
}

export default Draw;